/**
 * ExploreUsaLanding — /explore/usa
 * Mirrors ExploreIndiaLanding but scoped to US regions.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Globe2, Sparkles, MapPin, TrendingUp, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function pretty(k) {
  return String(k || "").replace(/^us-/, "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export default function ExploreUsaLanding() {
  const [d, setD] = useState(null);
  const [loading, setLoad] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/geo/explore/usa`)
      .then((r) => setD(r.data)).catch(() => setD(null))
      .finally(() => setLoad(false));
  }, []);

  return (
    <main className="max-w-5xl mx-auto px-5 lg:px-8 py-10" data-testid="explore-usa-landing">
      <nav className="text-xs opacity-70 mb-4">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-2">/</span><span>Explore USA</span>
      </nav>
      <div className="flex items-center gap-2 mb-2">
        <Globe2 className="w-5 h-5" />
        <h1 className="font-display text-3xl sm:text-4xl">Explore USA — live</h1>
      </div>
      <p className="text-sm opacity-70 mb-8 max-w-2xl">
        Every US location, POI, and topic on Social Pulse is generated and
        indexed automatically. Refresh any time for a fresh snapshot.
      </p>

      {loading && <div className="text-sm opacity-70 py-10"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}

      {d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="explore-usa-totals">
            {[
              { label: "Locations", value: d.totals.locations, color: "#1D4ED8" },
              { label: "POIs",      value: d.totals.pois,      color: "#B91C1C" },
              { label: "Topics",    value: d.totals.topics,    color: "#B45309" },
              { label: "States",    value: d.totals.regions,   color: "#065F46" },
            ].map((k) => (
              <div key={k.label} className="rounded-2xl border p-4"
                style={{ borderColor: `${k.color}33`, background: "var(--wofy-surface)" }}
                data-testid={`explore-usa-total-${k.label.toLowerCase()}`}
              >
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: k.color }}>{k.label}</div>
                <div className="text-2xl sm:text-3xl font-display mt-1 tabular-nums">{k.value.toLocaleString()}</div>
              </div>
            ))}
          </div>

          {d.recent_sweeps?.length > 0 && (
            <section className="mb-10" data-testid="explore-usa-recent-sweeps">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" />
                <h2 className="font-display text-xl">States completed this week</h2>
                <span className="text-xs opacity-60">{d.recent_sweeps.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {d.recent_sweeps.map((s) => (
                  <Link key={s.key} to={`/l/united-states/${s.key.replace(/^us-/, "")}`}
                    className="inline-flex items-center gap-1 text-sm rounded-full border px-3 py-1.5 hover:bg-black/[0.04] transition"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`explore-usa-recent-${s.key}`}
                  >
                    <span className="font-medium">{pretty(s.key)}</span>
                    <span className="text-[10px] opacity-60">{s.total} cities</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {d.top_cities?.length > 0 && (
            <section className="mb-10" data-testid="explore-usa-top-cities">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4" />
                <h2 className="font-display text-xl">Top US cities by POI count</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {d.top_cities.map((c) => (
                  <Link key={c.slug} to={`/l/${c.slug}`}
                    className="rounded-xl border p-3 hover:shadow-md transition"
                    style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                    data-testid={`explore-usa-city-${c.slug}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs opacity-70 mt-0.5">{c.state || c.slug}</div>
                      </div>
                      <span className="text-xs font-mono px-2 py-0.5 rounded-full border tabular-nums"
                        style={{ borderColor: "var(--wofy-border)" }}>{c.pois}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {d.trending_topics?.length > 0 && (
            <section className="mb-10" data-testid="explore-usa-trending">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4" />
                <h2 className="font-display text-xl">Trending US topics</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {d.trending_topics.map((t) => (
                  <Link key={t.slug} to={t.deep_link || `/topic/${t.slug.split("-").slice(1).join("-")}`}
                    className="rounded-2xl border overflow-hidden group"
                    style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                    data-testid={`explore-usa-trending-${t.slug}`}
                  >
                    <div className="aspect-video bg-black/5 overflow-hidden">
                      {t.hero_image?.url && (
                        <img src={t.hero_image.url} alt="" loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition" />
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{t.source || "topic"}</div>
                      <div className="font-medium mt-0.5 group-hover:underline">{t.title}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
